import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { notification } from 'antd';
import { Flag, Info, Link2, Loader2, TriangleAlert, X } from 'lucide-react';

import useMediaQuery from '../../../core/hooks/useMediaQuery';
import QuestionReportService from '../../../sdk/services/QuestionReportService';
import type { CreateReportBody } from '../../../sdk/services/QuestionReportService';
import type { ReportCategory } from '../../../sdk/@types/Forum';

/** Abaixo disto o formulário é uma bottom sheet; acima, um modal centralizado. */
export const PHONE_QUERY = '(max-width: 767px)';

export const MAX_DESCRIPTION = 1000;

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-surface';

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

interface CategoryOption {
  value: ReportCategory;
  label: string;
  description?: string;
}

/**
 * Os cinco motivos do design (frame `xE3Tm`). A ordem não é alfabética: vem do
 * mais frequente e mais grave ("Gabarito incorreto") para o mais aberto
 * ("Outro motivo"), que é o único sem descrição porque o aluno é quem explica.
 */
export const REPORT_CATEGORIES: CategoryOption[] = [
  {
    value: 'wrong_answer',
    label: 'Gabarito incorreto',
    description: 'A alternativa indicada como correta está errada',
  },
  {
    value: 'outdated_law',
    label: 'Lei ou norma desatualizada',
    description: 'A legislação mudou depois da prova',
  },
  {
    value: 'statement_error',
    label: 'Erro no enunciado ou nas alternativas',
    description: 'Digitação, texto faltando ou trocado',
  },
  { value: 'annulled', label: 'Questão anulada pela banca' },
  { value: 'other', label: 'Outro motivo' },
];

/**
 * Identificação da questão mostrada no cabeçalho — "Questão 1 · CBMGO 2024 ·
 * Noções de direito".
 *
 * É um objeto solto em vez da `Questao` inteira de propósito: o modal só
 * precisa do rótulo, e assim o carrossel monta o componente sem que ele
 * carregue nada por conta própria.
 */
export interface QuestionReportReference {
  /** Número exibido ao aluno (a posição no treino), não o id. */
  number?: number | string;
  /** Banca. */
  agency?: string;
  year?: number | string;
  /** Matéria. */
  subject?: string;
}

export interface ReportQuestionModalProps {
  open: boolean;
  /** Id da questão — vai direto na rota `POST /questions/:id/reports`. */
  questionId: string;
  reference: QuestionReportReference;
  onClose: () => void;
  /** Chamado depois de a denúncia ser registrada, antes de fechar. */
  onSubmitted?: () => void;
  /**
   * Costura para stories e testes. O padrão é o serviço real — não passe isto
   * no app.
   */
  submitReport?: (questionId: string, body: CreateReportBody) => Promise<unknown>;
}

export function referenceLabel(reference: QuestionReportReference): string {
  const agencyYear = [reference.agency, reference.year].filter(Boolean).join(' ');
  return [
    reference.number !== undefined && reference.number !== null && reference.number !== ''
      ? `Questão ${reference.number}`
      : null,
    agencyYear || null,
    reference.subject || null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function statusOf(error: unknown): number | undefined {
  const candidate = error as { response?: { status?: number }; apiError?: { status?: number } };
  return candidate?.response?.status ?? candidate?.apiError?.status;
}

const surface = (token: string) => `rgb(var(${token}))`;

/**
 * Confirmação de recebimento.
 *
 * Mesma armadilha documentada em `notifyDenial`: a API estática do antd
 * renderiza fora do único `ConfigProvider` do app, então o corpo é pintado com
 * as classes Tailwind (que leem os tokens do `html.dark` direto) e a casca
 * recebe `style` com os mesmos tokens. Sem isso o toast nasce claro no tema
 * escuro.
 */
export function notifyReportSent(): void {
  notification.open({
    key: 'question-report-sent',
    message: (
      <div className="font-sans">
        <p className="m-0 text-sm font-semibold text-ink">Denúncia enviada</p>
        <p className="m-0 mt-1 text-[13px] leading-snug text-ink-secondary">
          Um administrador vai analisar esta questão. Você recebe uma notificação quando houver
          uma resposta.
        </p>
      </div>
    ),
    description: null,
    placement: 'topRight',
    duration: 6,
    closeIcon: <X size={16} style={{ color: surface('--c-text-muted') }} />,
    style: {
      background: surface('--c-surface'),
      color: surface('--c-text'),
      border: `1px solid ${surface('--c-border')}`,
      borderRadius: 14,
      boxShadow: 'var(--shadow-panel)',
      padding: 16,
    },
  });
}

/**
 * Formulário de denúncia de questão — modal no desktop, bottom sheet no mobile.
 *
 * Autocontido de propósito (`questionId` + `reference`): o carrossel só monta e
 * controla `open`, sem carregar nada nem saber do serviço.
 *
 * Duas decisões que fogem do que está desenhado / do resto do app:
 *
 * - **Sem `danger`.** O frame do Pencil pinta o ícone e o "Enviar denúncia" na
 *   família vermelha, mas denunciar não é ação destrutiva para quem envia — o
 *   que destrói é a decisão do admin de desativar a questão. Aqui o CTA é
 *   `bg-brand` e o selo do cabeçalho é `warn`; `danger` fica só nas mensagens
 *   de validação, onde é semântica de erro de verdade (§7).
 * - **Casca própria em vez de `Modal`/`Drawer` do antd.** As duas apresentações
 *   vêm do mesmo painel só trocando as classes do contêiner, o `role="dialog"`
 *   fica com um elemento só (o do antd não tem nome acessível quando o header é
 *   nosso), e o componente não depende do `ConfigProvider` para ficar certo no
 *   tema escuro — o que importa no Storybook e nos testes, onde não existe um.
 */
export default function ReportQuestionModal({
  open,
  questionId,
  reference,
  onClose,
  onSubmitted,
  submitReport = QuestionReportService.createReport.bind(QuestionReportService),
}: ReportQuestionModalProps) {
  const sheet = useMediaQuery(PHONE_QUERY);

  const [category, setCategory] = useState<ReportCategory | null>(null);
  const [description, setDescription] = useState('');
  const [link, setLink] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyReported, setAlreadyReported] = useState(false);
  const [failed, setFailed] = useState(false);

  const ids = useId();
  const titleId = `${ids}-title`;
  const refId = `${ids}-ref`;
  const categoryErrorId = `${ids}-category-error`;
  const descriptionId = `${ids}-description`;
  const counterId = `${ids}-counter`;
  const descriptionErrorId = `${ids}-description-error`;
  const linkId = `${ids}-link`;

  const panelRef = useRef<HTMLDivElement | null>(null);
  const firstRadioRef = useRef<HTMLInputElement | null>(null);
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);
  const restoreFocusTo = useRef<Element | null>(null);
  const noticeRef = useRef<HTMLDivElement | null>(null);

  const missingCategory = attempted && category === null;
  const missingDescription = attempted && description.trim().length === 0;

  // Cada abertura começa limpa: o aluno que cancelou e voltou não herda um
  // motivo escolhido por engano, nem o aviso de duplicada da questão anterior.
  useEffect(() => {
    if (!open) return;
    setCategory(null);
    setDescription('');
    setLink('');
    setAttempted(false);
    setSubmitting(false);
    setAlreadyReported(false);
    setFailed(false);
  }, [open, questionId]);

  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement;
    panelRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      (restoreFocusTo.current as HTMLElement | null)?.focus?.();
    };
  }, [open]);

  // O aviso nasce no fim de um corpo rolável: sem isto o "Enviar denúncia"
  // fica desabilitado e o motivo está fora da tela.
  useEffect(() => {
    if (alreadyReported || failed) {
      noticeRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    }
  }, [alreadyReported, failed]);

  const requestClose = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [onClose, submitting]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        requestClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, requestClose]);

  if (!open) return null;

  /**
   * Prende o Tab dentro do painel. `aria-modal` só avisa o leitor de tela: sem
   * isto o teclado sai do formulário e vai parar nas alternativas da questão
   * por baixo, que continua montada (é um overlay, não uma rota).
   */
  const handlePanelKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;

    const focusables = Array.from(
      panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
      )
    ).filter((element) => element.getAttribute('aria-hidden') !== 'true');
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    // O painel em si só é alvo do foco na abertura (tabIndex -1): a partir dele
    // o Tab tem de entrar no formulário, não pular para fora.
    if (event.shiftKey ? active === first || active === panel : active === last) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (submitting || alreadyReported) return;

    // Um campo pendente é apontado onde ele está e recebe o foco — a mensagem
    // sozinha passa despercebida quando a sheet está rolada.
    if (category === null) {
      firstRadioRef.current?.focus();
      return;
    }
    if (description.trim().length === 0) {
      descriptionRef.current?.focus();
      return;
    }

    setFailed(false);
    setSubmitting(true);
    const trimmedLink = link.trim();
    try {
      await submitReport(questionId, {
        category,
        description: description.trim(),
        ...(trimmedLink ? { reference: trimmedLink } : {}),
      });
      notifyReportSent();
      onSubmitted?.();
      onClose();
    } catch (error) {
      // 409 = o próprio usuário já tem denúncia aberta nesta questão. Não é
      // falha: o formulário vira aviso e para de aceitar envio.
      if (statusOf(error) === 409) setAlreadyReported(true);
      else setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  const optionClasses = (selected: boolean) =>
    cx(
      'flex cursor-pointer items-start gap-3 rounded-input border px-3 py-2.5 transition-colors',
      selected
        ? 'border-select/[0.42] bg-select/[0.14]'
        : 'border-edge bg-surface hover:bg-surface-hover'
    );

  const fieldClasses = (invalid: boolean) =>
    cx(
      'mt-2 w-full rounded-input border bg-surface px-3 py-2 text-sm text-ink',
      'placeholder:text-ink-muted transition-colors',
      FOCUS_RING,
      invalid ? 'border-danger' : 'border-edge'
    );

  return createPortal(
    <div
      className={cx(
        'fixed inset-0 z-[1100] flex bg-black/50',
        // A margem acompanha a apresentação, não o breakpoint do Tailwind: entre
        // 640px e 767px `sm:p-4` afastaria a sheet da borda de baixo, e uma
        // bottom sheet solta no meio da tela não é uma bottom sheet.
        sheet ? 'items-end justify-center p-0' : 'items-center justify-center p-4'
      )}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={refId}
        data-presentation={sheet ? 'sheet' : 'modal'}
        tabIndex={-1}
        onKeyDown={handlePanelKeyDown}
        className={cx(
          'flex w-full flex-col overflow-hidden bg-surface text-ink shadow-panel outline-none',
          sheet
            ? 'max-h-[92dvh] rounded-t-modal'
            : 'max-h-[88vh] max-w-[560px] rounded-modal border border-edge'
        )}
      >
        {sheet ? (
          <div className="flex justify-center pt-2" aria-hidden="true">
            <span className="h-1 w-10 rounded-full bg-edge-strong" />
          </div>
        ) : null}

        <div className="flex items-start gap-3 border-b border-edge px-5 py-4">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-chip bg-warn/[0.14] text-warn-ink">
            <Flag size={18} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="m-0 text-base font-semibold text-ink">
              Denunciar questão
            </h2>
            <p id={refId} className="m-0 mt-0.5 text-[13px] text-ink-secondary">
              {referenceLabel(reference)}
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Fechar"
            className={cx(
              'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-chip',
              'text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink',
              FOCUS_RING
            )}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <form noValidate onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-4">
            {/* `aria-invalid` fica no grupo, não em cada radio: a role `radio`
                não suporta o atributo (é o grupo que está inválido, não uma
                opção), e o eslint-plugin-jsx-a11y reclama com razão. */}
            <fieldset
              className="m-0 border-0 p-0"
              role="radiogroup"
              aria-required="true"
              aria-invalid={missingCategory || undefined}
              aria-describedby={missingCategory ? categoryErrorId : undefined}
            >
              <legend className="mb-2 p-0 text-sm font-medium text-ink">Qual é o problema?</legend>
              <div className="flex flex-col gap-2">
                {REPORT_CATEGORIES.map((option, index) => {
                  const selected = category === option.value;
                  return (
                    <label key={option.value} className={optionClasses(selected)}>
                      <input
                        ref={index === 0 ? firstRadioRef : undefined}
                        type="radio"
                        name={`${ids}-category`}
                        value={option.value}
                        checked={selected}
                        onChange={() => setCategory(option.value)}
                        className={cx(
                          // O radio nativo ignora os tokens: em tema escuro o
                          // Chrome pinta a bolinha branca (a página não declara
                          // `color-scheme`), e as cinco opções parecem marcadas.
                          // `appearance-none` devolve o desenho para as classes.
                          'mt-0.5 h-4 w-4 shrink-0 appearance-none rounded-full bg-surface',
                          'transition-colors',
                          selected ? 'border-[5px] border-select' : 'border border-edge-strong',
                          FOCUS_RING
                        )}
                      />
                      <span className="min-w-0">
                        <span
                          className={cx(
                            'block text-sm font-medium',
                            selected ? 'text-select-ink' : 'text-ink'
                          )}
                        >
                          {option.label}
                        </span>
                        {option.description ? (
                          <span className="mt-0.5 block text-[12px] leading-snug text-ink-secondary">
                            {option.description}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
              {missingCategory ? (
                <p id={categoryErrorId} role="alert" className="m-0 mt-2 text-[13px] text-danger-ink">
                  Escolha o motivo da denúncia.
                </p>
              ) : null}
            </fieldset>

            <div>
              <div className="flex items-baseline justify-between gap-2">
                <label htmlFor={descriptionId} className="text-sm font-medium text-ink">
                  Descreva o problema
                </label>
                <span id={counterId} className="text-[12px] tabular-nums text-ink-muted">
                  {description.length}/{MAX_DESCRIPTION}
                </span>
              </div>
              <textarea
                id={descriptionId}
                ref={descriptionRef}
                rows={5}
                maxLength={MAX_DESCRIPTION}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Explique o que está errado — quanto mais específico, mais rápido o administrador resolve."
                aria-invalid={missingDescription || undefined}
                aria-describedby={cx(
                  counterId,
                  missingDescription ? descriptionErrorId : undefined
                )}
                className={cx(fieldClasses(missingDescription), 'resize-y')}
              />
              {missingDescription ? (
                <p
                  id={descriptionErrorId}
                  role="alert"
                  className="m-0 mt-1 text-[13px] text-danger-ink"
                >
                  Descreva o problema para o administrador entender a denúncia.
                </p>
              ) : null}
            </div>

            <div>
              <div className="flex items-baseline justify-between gap-2">
                <label htmlFor={linkId} className="text-sm font-medium text-ink">
                  Referência
                </label>
                <span className="text-[12px] text-ink-muted">opcional</span>
              </div>
              <div className="relative">
                <Link2
                  size={16}
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 mt-1 -translate-y-1/2 text-ink-muted"
                />
                <input
                  id={linkId}
                  type="url"
                  value={link}
                  onChange={(event) => setLink(event.target.value)}
                  placeholder="Link do gabarito oficial, artigo de lei…"
                  className={cx(fieldClasses(false), 'pl-9')}
                />
              </div>
            </div>

            {alreadyReported ? (
              <div
                ref={noticeRef}
                role="alert"
                className="flex items-start gap-2 rounded-input border border-warn/40 bg-warn/[0.12] p-3"
              >
                <TriangleAlert size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-warn-ink" />
                <div className="min-w-0">
                  <p className="m-0 text-[13px] font-semibold text-ink">Denúncia já em análise</p>
                  <p className="m-0 mt-1 text-[13px] leading-snug text-ink-secondary">
                    Você já enviou uma denúncia para esta questão. Um administrador vai analisar e
                    avisar você — não é preciso enviar outra.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-input bg-surface-muted p-3">
                <Info size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-ink-secondary" />
                <p className="m-0 text-[13px] leading-snug text-ink-secondary">
                  Um administrador vai analisar sua denúncia. Se ela for procedente, a questão é
                  desativada para todos e você recebe uma notificação.
                </p>
              </div>
            )}

            {failed ? (
              <div ref={noticeRef} role="alert">
                <p className="m-0 text-[13px] text-danger-ink">
                  Não foi possível enviar sua denúncia agora. Tente de novo em instantes — o texto
                  continua aqui.
                </p>
              </div>
            ) : null}
          </div>

          <div
            className={cx(
              'flex gap-2 border-t border-edge bg-surface-muted px-5 py-3',
              sheet ? 'flex-col-reverse' : 'items-center justify-end'
            )}
          >
            <button
              type="button"
              onClick={requestClose}
              className={cx(
                'inline-flex h-10 items-center justify-center rounded-input border border-edge',
                'bg-surface px-4 text-sm font-medium text-ink transition-colors hover:bg-surface-hover',
                FOCUS_RING
              )}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || alreadyReported}
              className={cx(
                'inline-flex h-10 items-center justify-center gap-2 rounded-input bg-brand px-4',
                'text-sm font-semibold text-brand-contrast transition-opacity hover:opacity-90',
                'disabled:cursor-not-allowed disabled:opacity-50',
                FOCUS_RING
              )}
            >
              {submitting ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : null}
              Enviar denúncia
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
